import { Typography } from "@mui/material"

/**
 * Circular progress indicator component.
 *
 * @component
 * @param {Object} props - The props for Progress.
 * @param {Number} [props.size=150] - The diameter of the progress circle in pixels.
 * @param {Number} [props.progress=0] - The progress value (0-100).
 * @param {Number} [props.trackWidth=10] - The width of the background track.
 * @param {String} [props.secondaryColor="#ddd"] - The color of the background track.
 * @param {Number} [props.indicatorWidth=10] - The width of the progress indicator.
 * @param {String} [props.color="#273679"] - The color of the progress indicator.
 * @param {String} [props.indicatorCap="round"] - The stroke linecap for the indicator (e.g., "round", "butt").
 * @param {String} [props.label="Progress"] - The label to display inside the circle.
 * @param {String} [props.labelColor="#333"] - The color of the label text.
 * @param {Boolean} [props.spinnerMode=false] - If true, shows a spinning animation instead of progress.
 * @param {Number} [props.spinnerSpeed=1] - The speed of the spinner animation (seconds per rotation).
 * @param {Boolean} [props.showLabel=false] - Whether to show the label text.
 * @param {Boolean} [props.showProgress=false] - Whether to show the progress in text text.
 * @param {React.CSSProperties} [props.labelStyles={}] - Label text styles.
 * @param {React.CSSProperties} [props.progressTextStyles={}] - Progress text styles.
 * @param {React.ReactNode} [props.icon] - Optional icon to display in the center instead of the label.
 * @returns {JSX.Element} The rendered Progress component.
 */
const Progress = ( {
    size = 150,
    progress = 0,
    trackWidth = 10,
    secondaryColor = `#ddd`,
    indicatorWidth = 10,
    color = `#8FA0EC`,
    indicatorCap = `round`,
    label = `Progress`,
    labelColor = `#333`,
    spinnerMode = false,
    spinnerSpeed = 1,
    showLabel = false,
    showProgress = false,
    icon = undefined,
    labelStyles = {},
    progressTextStyles = {}
} ) => {


    const center = size / 2,
        radius = center - ( trackWidth > indicatorWidth ? trackWidth : indicatorWidth ),
        dashArray = 2 * Math.PI * radius,
        dashOffset = dashArray * ( ( 100 - progress ) / 100 )

    let hideLabel = ( size < 50 || !label.length || spinnerMode ) ? true : false

    return (
        <>
            <div
                className="svg-pi-wrapper"
                style={{ width: size, height: size }}
            >
                <svg
                    className="svg-pi"
                    style={{ width: size, height: size }}
                >
                    <circle
                        className="svg-pi-track"
                        cx={center}
                        cy={center}
                        fill="transparent"
                        r={radius}
                        stroke={secondaryColor}
                        strokeWidth={trackWidth}
                    />
                    <circle
                        className={`svg-pi-indicator ${spinnerMode ? "svg-pi-indicator--spinner" : ""
                            }`}
                        style={{ animationDuration: spinnerSpeed * 1000 }}
                        cx={center}
                        cy={center}
                        fill="transparent"
                        r={radius}
                        stroke={color}
                        strokeWidth={indicatorWidth}
                        strokeDasharray={dashArray}
                        strokeDashoffset={dashOffset}
                        strokeLinecap={indicatorCap}
                    />
                </svg>

                {icon && <div
                    className="svg-pi-label"
                    style={{ color: labelColor }}
                >
                    <Typography variant="body2" className="svg-pi-label__loading">
                        {icon}
                    </Typography>

                </div>}

                {!icon && (
                    <div
                        className="svg-pi-label"
                        style={{ color: labelColor }}
                    >
                        {showLabel && <Typography
                            variant="body2"
                            className="svg-pi-label__loading"
                            sx={[{
                                fontSize: "12px"
                            }, ...(Array.isArray(labelStyles) ? labelStyles : [labelStyles])]}>
                            {label}
                        </Typography>}


                        {showProgress && <Typography
                            variant="body2"
                            className="svg-pi-label__progress"
                            sx={[{
                                fontSize: "12px"
                            }, ...(Array.isArray(progressTextStyles) ? progressTextStyles : [progressTextStyles])]}>
                            {`${progress > 100 ? 100 : progress
                                }%`}
                        </Typography>}

                    </div>
                )}
            </div>
        </>
    );

    // return (
    //     <div>
    //         <Box id="circular-progress" sx={{ transition: "background 1s ease", background: `conic-gradient(${color} ${progress * 3.6}deg,${secondaryColor} 0deg)` }} position="relative" width={`${size}px`} height={`${size}px`} bgcolor="#7d2ae8" borderRadius="50%" display="flex" alignItems="center" justifyContent="center">
    //             {!isNaN( progress ) && <Typography bgcolor="#fff" width={`${size * 0.8}px`} borderRadius="50%" fontSize={`${size * 0.3}px`} display="flex" alignItems="center" justifyContent="center" height={`${size * 0.8}px`} fontWeight="bold">{`${progress}%`}</Typography>}
    //         </Box>
    //     </div >
    // )
}

export default Progress
